import React, { useState, useEffect } from 'react';
import { Select, Divider, Radio } from 'antd';
import { getFirestore, collection, query, where, getDocs, doc, getDoc, updateDoc } from "firebase/firestore";
import { showSuccessNotification, showWarningNotification, showErrorNotification } from '../../../utils/notifications';
import MediumModal from '../../../components/Components/Modals/MediumModal';

const { Option } = Select;

function AddNewTag({
    isVisible,
    onClose,
    productId,
    productData,
    categories
}) {
    const [selectedAddTagId, setSelectedAddTagId] = useState('');
    const [selectedNewTagId, setSelectedNewTagId] = useState('');
    const [availableGroups, setAvailableGroups] = useState([]);
    const [availableTagsForGroup, setAvailableTagsForGroup] = useState([]);
    const db = getFirestore();

    // Carica i gruppi di tag quando la modale è aperta
    useEffect(() => {
        if (isVisible) {
            const fetchGroups = async () => {
                try {
                    const querySnapshot = await getDocs(collection(db, "Tags"));
                    const groups = Array.from(new Set(querySnapshot.docs.map(doc => doc.data().group)));
                    const filteredGroups = groups.filter(group => group !== 'Line' && group !== 'Featured');
                    setAvailableGroups(filteredGroups);
                } catch (error) {
                    console.error("Error in fetching groups:", error);
                    showErrorNotification("Error", "Error in fetching groups");
                }
            };
            fetchGroups();
        }
    }, [isVisible, db]);

    // Resetta gli stati quando la modale viene chiusa
    useEffect(() => {
        if (!isVisible) {
            setSelectedAddTagId('');
            setSelectedNewTagId('');
            setAvailableTagsForGroup([]);
        }
    }, [isVisible]);

    // Carica i tag per il gruppo selezionato
    useEffect(() => {
        const fetchTagsForGroup = async () => {
            if (!selectedAddTagId) return;

            try {
                const q = query(
                    collection(db, "Tags"),
                    where("group", "==", selectedAddTagId)
                );

                const querySnapshot = await getDocs(q);
                const tags = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

                setAvailableTagsForGroup(tags);
            } catch (error) {
                console.error("Error in fetching tags:", error);
                showErrorNotification("Error", "Error in fetching tags");
            }
        };

        fetchTagsForGroup();
    }, [selectedAddTagId, db]);

    // Funzione per gestire l'aggiunta di un nuovo tag
    const handleAddTag = async () => {
        try {
            if (!selectedNewTagId) {
                showWarningNotification("Attention", "Select a tag to add");
                return;
            }

            // Utilizziamo direttamente l'ID del tag invece del path completo
            const tagId = selectedNewTagId;

            // Ottieni i dati più recenti del prodotto direttamente dal database
            const productRef = doc(db, "Products", productId);
            const productSnapshot = await getDoc(productRef);

            if (!productSnapshot.exists()) {
                showErrorNotification("Error", "Product not found");
                return;
            }

            // Usa i dati più recenti del prodotto
            const currentProductData = productSnapshot.data();
            const currentCategories = currentProductData.list_tags || [];

            // Verifica se il tag è già presente (solo controllo dell'ID)
            if (currentCategories.includes(tagId)) {
                showWarningNotification("Attention", "This tag has already been added to the product");
                return;
            }

            // Recupera i dati del nuovo tag per mostrare feedback all'utente
            const tagRef = doc(db, "Tags", tagId);
            const tagSnapshot = await getDoc(tagRef);

            if (!tagSnapshot.exists()) {
                showErrorNotification("Error", "Tag not found in the database");
                return;
            }

            const tagData = tagSnapshot.data();

            // Aggiorna il database aggiungendo solo l'ID del tag
            const updatedCategories = [...currentCategories, tagId];
            await updateDoc(productRef, { list_tags: updatedCategories });

            showSuccessNotification("Save successful", `Tag "${tagData.name}" added successfully!`);

            // Resetta gli stati
            setSelectedAddTagId('');
            setSelectedNewTagId('');
            setAvailableTagsForGroup([]);

            // Chiudi la modale e notifica il componente padre dell'aggiornamento
            onClose(true);
        } catch (error) {
            console.error("Error in adding the tag:", error);
            showErrorNotification("Error", "Error in adding the tag");
        }
    };

    return (
        <MediumModal
            title="Add a Tag"
            visible={isVisible}
            onClose={() => onClose(false)}
            onConfirm={handleAddTag}
            confirmText="Add"
            cancelText="Cancel"
            styles={{ padding: '20px' }}
            width={700}
        >
            <Select
                placeholder="Select a group"
                onChange={value => setSelectedAddTagId(value)}
                style={{ width: '100%', marginBottom: '16px', borderRadius: '8px' }}
                value={selectedAddTagId || undefined}
            >
                {availableGroups.map((group, index) => (
                    <Option key={`add-group-${group}-${index}`} value={group}>
                        {group}
                    </Option>
                ))}
            </Select>

            <Divider />

            {selectedAddTagId ? (
                <Radio.Group
                    style={{
                        width: '100%',
                        marginTop: '16px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '8px'
                    }}
                    value={selectedNewTagId}
                    onChange={e => setSelectedNewTagId(e.target.value)}
                >
                    {availableTagsForGroup.map(tag => (
                        <Radio key={tag.id} value={tag.id}
                            style={{
                                padding: '8px',
                                borderRadius: '8px',
                                backgroundColor: '#f0f2f5',
                                textAlign: 'center'
                            }}
                        >
                            {tag.name}
                        </Radio>
                    ))}
                </Radio.Group>
            ) : (
                <div style={{ marginTop: '16px', color: '#888' }}>
                    Select a group to see available options.
                </div>
            )}
        </MediumModal>
    );
}

export default AddNewTag;